/**
 * DentalChart — SVGs exactos del proyecto dental original (vista oclusal).
 * Referencia: `ilustraciones.md` compartido por el usuario.
 *
 * 6 tipos de diente: central_incisor, lateral_incisor, canine, premolar, molar, wisdom.
 * Estados: normal / missing (✕ rojo) / implant (⬡ azul) / prosthesis (SVG en purpura).
 */
import { useMemo } from 'react';

const FDI_UPPER = ["18","17","16","15","14","13","12","11","21","22","23","24","25","26","27","28"];
const FDI_LOWER = ["48","47","46","45","44","43","42","41","31","32","33","34","35","36","37","38"];
const FDI_TO_UNIVERSAL = {
  "18":"1","17":"2","16":"3","15":"4","14":"5","13":"6","12":"7","11":"8",
  "21":"9","22":"10","23":"11","24":"12","25":"13","26":"14","27":"15","28":"16",
  "38":"17","37":"18","36":"19","35":"20","34":"21","33":"22","32":"23","31":"24",
  "41":"25","42":"26","43":"27","44":"28","45":"29","46":"30","47":"31","48":"32",
};

const STATUS_CYCLE = ["normal", "missing", "prosthesis", "implant_single", "implant_multi", "crown", "veneer"];
const STATUS_LABEL_ES = {
  normal: "Normal", missing: "Ausente", prosthesis: "Protesis",
  implant: "Implante",            // legacy — se muestra como Implante Unitario
  implant_single: "Implante Unitario",
  implant_multi: "Implante Multiple",
  crown: "Corona", veneer: "Carilla",
};

// Clasificacion FDI → tipo
function _toothType(fdi) {
  const d = parseInt(String(fdi)[1], 10);
  if (d === 1) return 'central_incisor';
  if (d === 2) return 'lateral_incisor';
  if (d === 3) return 'canine';
  if (d === 4 || d === 5) return 'premolar';
  if (d === 6 || d === 7) return 'molar';
  return 'wisdom';   // 8 = tercer molar
}

function _isUpperTooth(fdi) {
  const q = parseInt(String(fdi)[0], 10);
  return q === 1 || q === 2;
}

// SVGs del proyecto dental original (fill/stroke se aplican via CSS currentColor).
function ToothSVG({ type, color }) {
  const stroke = color;
  const fill = color;
  const p = {
    strokeWidth: 1.5,
    strokeLinejoin: 'round',
  };
  if (type === 'central_incisor') {
    return (
      <svg viewBox="0 0 40 50" width="34" height="42" aria-hidden="true">
        <path d="M8 5 Q20 0 32 5 Q35 15 34 30 Q32 45 20 48 Q8 45 6 30 Q5 15 8 5" fill={fill} stroke={stroke} {...p} />
        <ellipse cx="20" cy="20" rx="8" ry="10" fill="white" opacity="0.3" />
      </svg>
    );
  }
  if (type === 'lateral_incisor') {
    return (
      <svg viewBox="0 0 40 50" width="32" height="40" aria-hidden="true">
        <path d="M10 5 Q20 2 30 5 Q33 15 32 28 Q30 42 20 46 Q10 42 8 28 Q7 15 10 5" fill={fill} stroke={stroke} {...p} />
        <ellipse cx="20" cy="18" rx="6" ry="8" fill="white" opacity="0.3" />
      </svg>
    );
  }
  if (type === 'canine') {
    return (
      <svg viewBox="0 0 40 55" width="32" height="44" aria-hidden="true">
        <path d="M10 5 Q20 0 30 5 Q34 15 32 30 Q28 48 20 52 Q12 48 8 30 Q6 15 10 5" fill={fill} stroke={stroke} {...p} />
        <path d="M20 8 L20 25" stroke="white" strokeWidth="2" opacity="0.4" />
        <ellipse cx="20" cy="15" rx="5" ry="6" fill="white" opacity="0.25" />
      </svg>
    );
  }
  if (type === 'premolar') {
    return (
      <svg viewBox="0 0 40 45" width="38" height="42" aria-hidden="true">
        <path d="M8 8 Q12 3 20 3 Q28 3 32 8 Q36 15 35 25 Q33 38 20 42 Q7 38 5 25 Q4 15 8 8" fill={fill} stroke={stroke} {...p} />
        <ellipse cx="14" cy="15" rx="5" ry="6" fill="white" opacity="0.35" />
        <ellipse cx="26" cy="15" rx="5" ry="6" fill="white" opacity="0.35" />
        <line x1="20" y1="10" x2="20" y2="22" stroke="white" strokeWidth="1.5" opacity="0.4" />
      </svg>
    );
  }
  if (type === 'molar') {
    return (
      <svg viewBox="0 0 50 45" width="46" height="42" aria-hidden="true">
        <path d="M5 10 Q10 3 25 3 Q40 3 45 10 Q48 18 47 28 Q44 40 25 43 Q6 40 3 28 Q2 18 5 10" fill={fill} stroke={stroke} {...p} />
        <ellipse cx="15" cy="13" rx="6" ry="5" fill="white" opacity="0.35" />
        <ellipse cx="35" cy="13" rx="6" ry="5" fill="white" opacity="0.35" />
        <ellipse cx="15" cy="25" rx="5" ry="5" fill="white" opacity="0.35" />
        <ellipse cx="35" cy="25" rx="5" ry="5" fill="white" opacity="0.35" />
        <ellipse cx="25" cy="19" rx="4" ry="4" fill="white" opacity="0.25" />
        <line x1="25" y1="8" x2="25" y2="32" stroke="white" strokeWidth="1" opacity="0.4" />
        <line x1="8" y1="19" x2="42" y2="19" stroke="white" strokeWidth="1" opacity="0.4" />
      </svg>
    );
  }
  // wisdom (tercer molar)
  return (
    <svg viewBox="0 0 48 45" width="44" height="42" aria-hidden="true">
      <path d="M6 12 Q12 4 24 4 Q36 4 42 12 Q46 20 44 30 Q40 42 24 44 Q8 42 4 30 Q2 20 6 12" fill={fill} stroke={stroke} {...p} />
      <ellipse cx="14" cy="14" rx="5" ry="4" fill="white" opacity="0.35" />
      <ellipse cx="34" cy="14" rx="5" ry="4" fill="white" opacity="0.35" />
      <ellipse cx="14" cy="26" rx="4" ry="5" fill="white" opacity="0.35" />
      <ellipse cx="34" cy="26" rx="4" ry="5" fill="white" opacity="0.35" />
      <ellipse cx="24" cy="20" rx="5" ry="5" fill="white" opacity="0.25" />
      <path d="M24 8 Q22 20 24 34" stroke="white" strokeWidth="1" opacity="0.4" fill="none" />
      <path d="M8 20 Q24 22 40 20" stroke="white" strokeWidth="1" opacity="0.4" fill="none" />
    </svg>
  );
}

// Colores por estado (fill del SVG)
const COLOR_NORMAL       = '#334155';   // slate-700
const COLOR_MISSING      = '#f87171';   // red-400
const COLOR_IMPLANT      = '#60a5fa';   // blue-400 (unitario / legacy)
const COLOR_IMPLANT_MULT = '#4f46e5';   // indigo-600 (multiple)
const COLOR_PROSTHESIS   = '#c084fc';   // purple-400
const COLOR_CROWN        = '#f59e0b';   // amber-500
const COLOR_VENEER       = '#14b8a6';   // teal-500
const COLOR_NOT_INCLUDED = '#cbd5e1';   // slate-300 tenue

function StatusOverlay({ status }) {
  // Reemplaza el diente por un simbolo grande cuando esta ausente o es implante
  if (status === 'missing') {
    return (
      <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
        <path d="M10 10 L30 40 M30 10 L10 40" stroke={COLOR_MISSING} strokeWidth="4" strokeLinecap="round" />
      </svg>
    );
  }
  if (status === 'implant' || status === 'implant_single' || status === 'implant_multi') {
    const isMulti = status === 'implant_multi';
    const fill = isMulti ? COLOR_IMPLANT_MULT : COLOR_IMPLANT;
    return (
      <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
        <polygon
          points="20,4 34,15 34,35 20,46 6,35 6,15"
          fill={fill}
          fillOpacity={isMulti ? '0.35' : '0.25'}
          stroke={fill}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {/* URS-F446: barra horizontal para indicar visualmente que forma
            parte de una restauracion multi-implante (no aparece en unitario). */}
        {isMulti && (
          <line x1="4" y1="25" x2="36" y2="25" stroke={fill} strokeWidth="3" strokeLinecap="round" />
        )}
      </svg>
    );
  }
  return null;
}

function Tooth({ number, notation, status, selected, completed, disabled, onClick, mode, included }) {
  const isConfig = mode === 'config';
  const type = _toothType(number);
  const upper = _isUpperTooth(number);
  const label = notation === 'Universal' ? (FDI_TO_UNIVERSAL[number] || number) : number;

  const bg =
    isConfig
      ? (included ? 'bg-blue-50' : 'bg-white')
      : status === 'missing'          ? 'bg-red-50'
      : status === 'implant'          ? 'bg-blue-50'
      : status === 'implant_single'   ? 'bg-blue-50'
      : status === 'implant_multi'    ? 'bg-indigo-50'
      : status === 'prosthesis'       ? 'bg-purple-50'
      : status === 'crown'            ? 'bg-amber-50'
      : status === 'veneer'           ? 'bg-teal-50'
      : selected                      ? 'bg-slate-900'
      : completed                     ? 'bg-emerald-50'
      : 'bg-white';

  const borderColor =
    isConfig
      ? (included ? 'border-blue-400' : 'border-dashed border-slate-300')
      : status === 'missing'          ? 'border-red-400'
      : status === 'implant'          ? 'border-blue-400'
      : status === 'implant_single'   ? 'border-blue-400'
      : status === 'implant_multi'    ? 'border-indigo-500'
      : status === 'prosthesis'       ? 'border-purple-400'
      : status === 'crown'            ? 'border-amber-400'
      : status === 'veneer'           ? 'border-teal-400'
      : selected                      ? 'border-slate-900'
      : completed                     ? 'border-emerald-500'
      : 'border-slate-300';

  const textColor = selected ? 'text-white' : 'text-slate-700';
  const dim = disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:shadow-md transition-shadow';

  const svgColor = isConfig
    ? (included ? COLOR_NORMAL : COLOR_NOT_INCLUDED)
    : status === 'prosthesis' ? COLOR_PROSTHESIS
    : status === 'crown'      ? COLOR_CROWN
    : status === 'veneer'     ? COLOR_VENEER
    : selected ? '#ffffff'
    : COLOR_NORMAL;

  // URS-F443: badge de una letra en la esquina para diferenciar Corona (C)
  // y Carilla (V) del resto de estados (que ya se distinguen con el color
  // completo del diente o los overlays de "ausente"/"implante").
  const cornerBadge =
    status === 'crown'  ? { letter: 'C', bg: '#f59e0b' }
    : status === 'veneer' ? { letter: 'V', bg: '#14b8a6' }
    : null;

  const title = isConfig
    ? `${number} — ${included ? 'Incluido en el estudio' : 'No incluido'} (click para ${included ? 'excluir' : 'incluir'})`
    : `${number} — ${STATUS_LABEL_ES[status] || 'Normal'}${mode === 'status' ? ' (click para cambiar)' : ''}`;

  // Los dientes inferiores se rotan 180° para orientacion anatomica
  const rotate = upper ? '' : 'rotate-180';

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={`dental-tooth-${number}`}
      className={`relative w-12 h-16 rounded-lg border-2 ${borderColor} ${bg} ${textColor} flex flex-col items-center justify-center px-1 ${dim}`}
      title={title}
    >
      <div className={rotate}>
        {(status === 'missing' || status === 'implant' || status === 'implant_single' || status === 'implant_multi') && !isConfig
          ? <StatusOverlay status={status} />
          : <ToothSVG type={type} color={svgColor} />}
      </div>
      <span className={`absolute -bottom-5 left-0 right-0 text-center font-mono text-[11px] font-semibold ${selected ? 'text-slate-900' : 'text-slate-600'}`}>
        {label}
      </span>
      {completed && !isConfig && (
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
      )}
      {cornerBadge && !isConfig && (
        <span
          className="absolute -top-1 -left-1 min-w-[16px] h-4 px-1 rounded text-white text-[10px] font-bold flex items-center justify-center border border-white"
          style={{ background: cornerBadge.bg }}
          data-testid={`dental-tooth-badge-${number}`}
        >
          {cornerBadge.letter}
        </span>
      )}
    </button>
  );
}

export default function DentalChart({
  notationSystem = 'FDI',
  teethIncluded = [],
  teethStatus = {},
  selectedTooth = null,
  completedTeeth = new Set(),
  onSelectTooth = () => {},
  onChangeStatus = null,
  mode = 'select',
  compact = false,
}) {
  const includedSet = useMemo(() => new Set(teethIncluded), [teethIncluded]);

  const handleClick = (tooth) => {
    if (mode === 'status' && onChangeStatus) {
      const current = teethStatus[tooth] || 'normal';
      const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
      onChangeStatus(tooth, next);
    } else {
      onSelectTooth(tooth);
    }
  };

  const renderRow = (list) => (
    <div className="flex items-end gap-1.5 justify-center pb-5">
      {list.map(t => (
        <Tooth
          key={t}
          number={t}
          notation={notationSystem}
          status={teethStatus[t] || 'normal'}
          selected={selectedTooth === t}
          completed={completedTeeth.has(t)}
          disabled={mode !== 'config' && !includedSet.has(t)}
          included={includedSet.has(t)}
          onClick={() => handleClick(t)}
          mode={mode}
        />
      ))}
    </div>
  );

  return (
    <div className={`inline-block ${compact ? 'scale-90' : ''}`} data-testid="dental-chart">
      <div className="flex flex-col gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
        {renderRow(FDI_UPPER)}
        <div className="h-px bg-slate-300 mx-4" />
        {renderRow(FDI_LOWER)}
      </div>
      {mode === 'config' && (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600 justify-center">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-50 border border-blue-400" /> Incluido en el estudio</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-white border border-dashed border-slate-300" /> No incluido</span>
        </div>
      )}
      {(mode === 'select' || mode === 'status') && (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600 justify-center">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-white border border-slate-300" /> Normal</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-50 border border-red-400" /> Ausente ✕</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-purple-50 border border-purple-400" /> Protesis</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-50 border border-blue-400" /> Implante unitario ⬡</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-indigo-50 border border-indigo-500" /> Implante multiple ⬢</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-50 border border-amber-400" /> Corona (C)</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-teal-50 border border-teal-400" /> Carilla (V)</span>
          {mode === 'select' && (
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-50 border border-emerald-500" /> Completo</span>
          )}
        </div>
      )}
    </div>
  );
}

export { FDI_UPPER as FDI_UPPER_RIGHT, FDI_UPPER as FDI_UPPER_LEFT, FDI_LOWER as FDI_LOWER_RIGHT, FDI_LOWER as FDI_LOWER_LEFT, FDI_TO_UNIVERSAL };
